import * as SwitchPrimitives from "@radix-ui/react-switch";
import * as React from "react";

import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { cn } from "@/lib/utils";
import { optionHoverClass } from "./option-row";

/**
 * Design 14i — a 36×20 track with a 16px thumb. A Switch takes effect the
 * moment it moves; anything that only counts once a form is submitted is a
 * Checkbox instead.
 */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>
>(({ className, onCheckedChange, ...props }, ref) => {
  const { markDirty } = useDialogGuard();

  return (
    <SwitchPrimitives.Root
      onCheckedChange={(checked) => {
        markDirty();
        onCheckedChange?.(checked);
      }}
      className={cn(
        optionHoverClass,
        "peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-[background-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input",
        className,
      )}
      {...props}
      ref={ref}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          "pointer-events-none block h-4 w-4 rounded-full bg-background shadow ring-0 transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0",
          // QL-151 — a disabled thumb goes flat and gray. With opacity alone, a disabled
          // unchecked switch looked exactly like an enabled one that was just off.
          "data-[disabled]:bg-muted-foreground data-[disabled]:shadow-none",
        )}
      />
    </SwitchPrimitives.Root>
  );
});
Switch.displayName = SwitchPrimitives.Root.displayName;

export { Switch };
